import { useState, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from 'components/v2/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useApi } from 'common/hooks/api';
import {
  addDays,
  addMonths,
  subMonths,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  eachDayOfInterval,
  format,
  isBefore,
  startOfDay,
  isSameMonth,
  addWeeks,
  getWeeksInMonth,
  parseISO,
} from 'date-fns';
import { ReactComponent as DateRangeIcon } from 'components/v2/Icons/svg/date_range.svg';
import ChevronLeftIcon from 'pages/ItineraryBuilder/icons/ChevronLeft';
import ChevronRightIcon from 'pages/ItineraryBuilder/icons/ChevronRight';

const CELL_SIZE = 36;
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const cellOuter = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  py: '2px',
  position: 'relative',
};

const cellBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  borderRadius: '50%',
  userSelect: 'none',
  width: CELL_SIZE,
  height: CELL_SIZE,
  position: 'relative',
  zIndex: 1,
};

const statusStyles = {
  available: {
    border: '1px solid',
    borderColor: 'greyBlue.base',
    backgroundColor: '#fff',
    color: 'text.primary',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  onRequest: {
    border: '1px solid',
    borderColor: 'greyBlue.base',
    backgroundColor: '#fff',
    color: 'text.primary',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  unavailable: {
    border: '1px solid',
    borderColor: 'error.main',
    backgroundColor: 'errorRed.white80',
    color: 'error.main',
  },
  loading: {
    backgroundColor: 'stoneGrey.white40',
    border: '1px solid',
    borderColor: 'stoneGrey.white40',
    color: 'text.secondary',
  },
  past: {
    color: 'text.disabled',
  },
  selected: {
    backgroundColor: '#FF6E00',
    border: '1px solid #CC5800',
    color: '#331600',
    cursor: 'pointer',
    '&:hover': { opacity: 0.9 },
  },
  selectedBetween: {
    backgroundColor: '#FF8B33',
    color: '#331600',
    cursor: 'pointer',
  },
  outOfMonth: {
    color: 'text.disabled',
  },
  hoverPreview: {
    backgroundColor: '#FF8B33',
    color: '#331600',
    cursor: 'pointer',
  },
};

function getTooltipText(status) {
  if (status === true) return 'Available';
  if (status === 'onRequest') return 'On Request';
  if (status === false) return 'Unavailable';
  return '';
}

export default function EdgeAvailabilityCalendar({
  savedItineraryId,
  selectedDate,
  onSelectDate,
  onRangeSelect,
  stayNights = 0,
  adults,
  childrenAges,
  initialMonth,
  skipAvailability = false,
}) {
  const Api = useApi();
  const [currentDate, setCurrentDate] = useState(() => initialMonth || new Date());
  const dragRef = useRef(null);
  const [dragOrigin, setDragOrigin] = useState(null);
  const [clickActive, setClickActive] = useState(false);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = format(startOfWeek(monthStart), 'yyyy-MM-dd');
  const endDate = format(endOfWeek(monthEnd), 'yyyy-MM-dd');
  const monthKey = format(currentDate, 'yyyy-MM');

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['edge-availability', savedItineraryId, monthKey, adults, childrenAges],
    queryFn: async () => {
      const response = await Api.getSavedItineraryAvailability(savedItineraryId, startDate, endDate, [], false, adults, childrenAges);
      return response.data;
    },
    enabled: !!savedItineraryId && !skipAvailability,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = !skipAvailability && queryLoading;
  const availability = data?.availability || {};
  const [hoverDate, setHoverDate] = useState(null);

  const rangeEndDate = useMemo(() => {
    if (!selectedDate || stayNights <= 0) return null;
    return format(addDays(parseISO(selectedDate), stayNights), 'yyyy-MM-dd');
  }, [selectedDate, stayNights]);

  const selectedDates = useMemo(() => {
    if (!selectedDate || stayNights <= 0) return new Set(selectedDate ? [selectedDate] : []);
    const start = parseISO(selectedDate);
    const end = addDays(start, stayNights);
    return new Set(eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd')));
  }, [selectedDate, stayNights]);

  const hoverAnchor = dragOrigin || selectedDate;
  const hoverBefore = hoverDate && hoverAnchor && hoverDate < hoverAnchor;

  const hoverDates = useMemo(() => {
    if (!hoverAnchor || (!dragOrigin && stayNights > 0) || !hoverDate || hoverDate === hoverAnchor) return new Set();
    const start = parseISO(hoverAnchor);
    const end = parseISO(hoverDate);
    const [from, to] = start < end ? [start, end] : [end, start];
    return new Set(eachDayOfInterval({ start: from, end: to }).map(d => format(d, 'yyyy-MM-dd')));
  }, [hoverAnchor, dragOrigin, stayNights, hoverDate]);

  const days = useMemo(() => {
    const start = startOfWeek(monthStart);
    const weeks = getWeeksInMonth(currentDate);
    const delta = 6 - weeks;
    const end = addWeeks(endOfWeek(monthEnd), delta);
    return eachDayOfInterval({ start, end });
  }, [currentDate, monthStart, monthEnd]);

  const today = startOfDay(new Date());

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box
          onClick={() => setCurrentDate(prev => startOfMonth(subMonths(prev, 1)))}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '& svg': { width: 9, height: 'auto' } }}
        >
          <ChevronLeftIcon />
        </Box>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1 }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          {isLoading && <CircularProgress size={12} color="secondary" />}
        </Stack>
        <Box
          onClick={() => setCurrentDate(prev => startOfMonth(addMonths(prev, 1)))}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '& svg': { width: 9, height: 'auto' } }}
        >
          <ChevronRightIcon />
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '5px',
          justifyItems: 'center',
          alignItems: 'center',
          mb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0.5,
        }}
      >
        {DAYS.map(d => (
          <Typography key={d} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {d}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: 'auto',
        }}
      >
        {days.map(day => {
          const dateString = format(day, 'yyyy-MM-dd');
          const inMonth = isSameMonth(day, currentDate);
          const isPast = isBefore(day, today);
          const isSelected = selectedDates.has(dateString);
          const isHovered = hoverDates.has(dateString);
          const status = availability[dateString];
          const hasData = status !== undefined;

          const isClickableDate = skipAvailability
            ? !isPast
            : inMonth && !isPast && (status === true || status === 'onRequest' || dateString === selectedDate);

          const isAnchor = dateString === selectedDate || dateString === rangeEndDate;

          let cellStyle;
          if (isSelected && isAnchor) {
            cellStyle = statusStyles.selected;
          } else if (isSelected) {
            cellStyle = statusStyles.selectedBetween;
          } else if (isHovered && isClickableDate) {
            cellStyle = statusStyles.hoverPreview;
          } else if (!inMonth) {
            cellStyle = skipAvailability && !isPast ? statusStyles.available : statusStyles.outOfMonth;
          } else if (isPast) {
            cellStyle = statusStyles.past;
          } else if (skipAvailability) {
            cellStyle = statusStyles.available;
          } else if (isLoading || !hasData) {
            cellStyle = statusStyles.loading;
          } else if (status === true) {
            cellStyle = statusStyles.available;
          } else if (status === 'onRequest') {
            cellStyle = statusStyles.onRequest;
          } else {
            cellStyle = statusStyles.unavailable;
          }

          const isStartDate = dateString === selectedDate;
          const isClickable = isClickableDate || isStartDate;

          const circle = <Box sx={{ ...cellBase, ...cellStyle }}>{format(day, 'd')}</Box>;

          const inRange = isSelected && stayNights > 0;
          const inHoverRange = hoverDates.has(dateString);
          const showHighlight = inRange || inHoverRange;
          const isRangeStart = dateString === selectedDate && showHighlight;
          const isRangeEnd = dateString === rangeEndDate && inRange;
          const isHoverEnd = inHoverRange && dateString === hoverDate;

          const bandHeight = CELL_SIZE - 2;
          let highlightSx = {};
          if (showHighlight) {
            const bg = inRange ? 'rgba(255,110,0,0.3)' : 'rgba(255,110,0,0.15)';
            const bandBase = {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              height: bandHeight,
              zIndex: 0,
            };
            const isAnchorStart = isRangeStart || (isStartDate && inHoverRange);
            const isAnchorEnd = isRangeEnd || isHoverEnd;
            if (isAnchorStart && isAnchorEnd) {
              highlightSx = {};
            } else if (isAnchorStart) {
              const side = inHoverRange && hoverBefore ? { left: 0, right: '50%' } : { left: '50%', right: 0 };
              highlightSx = { '&::after': { ...bandBase, ...side, backgroundColor: bg } };
            } else if (isAnchorEnd) {
              const side = inHoverRange && hoverBefore ? { left: '50%', right: 0 } : { left: 0, right: '50%' };
              highlightSx = { '&::after': { ...bandBase, ...side, backgroundColor: bg } };
            } else {
              highlightSx = { '&::after': { ...bandBase, left: 0, right: 0, backgroundColor: bg } };
            }
          }

          const handleMouseDown =
            onRangeSelect && isClickable
              ? e => {
                  e.preventDefault();
                  const wasClickActive = clickActive;
                  dragRef.current = { origin: dateString, moved: false, wasSecondClick: wasClickActive };
                  setDragOrigin(dateString);
                  if (!wasClickActive) {
                    onRangeSelect(dateString, null);
                    setClickActive(true);
                  }
                }
              : undefined;

          const handleMouseUp =
            onRangeSelect && isClickable
              ? () => {
                  if (dragRef.current?.moved) {
                    const a = dragRef.current.origin;
                    const b = dateString;
                    onRangeSelect(a < b ? a : b, a < b ? b : a);
                    setClickActive(false);
                  } else if (dragRef.current?.wasSecondClick) {
                    if (dateString === selectedDate) {
                      onRangeSelect(null, null);
                      setClickActive(false);
                    } else {
                      const a = selectedDate;
                      const b = dateString;
                      onRangeSelect(a < b ? a : b, a < b ? b : a);
                      setClickActive(false);
                    }
                  }
                  dragRef.current = null;
                  setDragOrigin(null);
                }
              : undefined;

          const handleMouseEnter = isClickable
            ? () => {
                setHoverDate(dateString);
                if (dragRef.current && dragRef.current.origin !== dateString) {
                  dragRef.current.moved = true;
                }
              }
            : undefined;

          const handleClick = isClickable
            ? () => {
                if (onRangeSelect) {
                  if (dragRef.current?.moved) return;
                  return;
                }
                onSelectDate(isStartDate ? null : dateString);
              }
            : undefined;

          const cell = (
            <Box
              key={dateString}
              onClick={handleClick}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setHoverDate(null)}
              sx={{ ...cellOuter, cursor: isClickable ? 'pointer' : 'default', ...highlightSx }}
            >
              {circle}
            </Box>
          );

          if (inMonth && !isPast && hasData && !isLoading) {
            return (
              <Tooltip key={dateString} title={getTooltipText(status)}>
                {cell}
              </Tooltip>
            );
          }

          return cell;
        })}
      </Box>

      {selectedDate && stayNights > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            mt: 1.5,
            px: 2,
            py: 1.5,
            backgroundColor: 'stoneGrey.white40',
            borderRadius: 1,
          }}
        >
          <Box sx={{ color: 'greyBlue.base', flexShrink: 0, display: 'flex' }}>
            <DateRangeIcon width={20} height={20} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {format(parseISO(selectedDate), 'EEE, MMM d yyyy')} to{' '}
              {format(addDays(parseISO(selectedDate), stayNights), 'EEE, MMM d yyyy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stayNights} nights / {stayNights + 1} days
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
